import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import FilterableDataGrid from '../../components/common/FilterableDataGrid';
import {
  Check, Save, X, Plus, Trash2, Pencil, RefreshCw, ListOrdered, ArrowUpDown,
  ChevronLeft, ChevronRight, GripVertical, Search, ClipboardList,
  List, Table, FilterX
} from 'lucide-react';


const STEP_TYPES = ['Core', 'Feature', 'Milestone', 'UI'];
const OWNERS = ['josh', 'danny', 'todd', 'jared'];

const DevPlanPage = () => {
  // ── Shared ───────────────────────────────────────────────────────────────────
  const [devPlan, setDevPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const currentUserSlug = currentUser?.email?.split('@')[0]?.split('.')[0]?.toLowerCase() || null;

  // ── List view state ──────────────────────────────────────────────────────────
  const [selectedStep, setSelectedStep] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [listCollapsed, setListCollapsed] = useState(false);
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStep, setNewStep] = useState({ name: '', description: '', comment: '', status: 'Not Started', type: 'Core', owner: currentUserSlug });
  const [showCloseStep, setShowCloseStep] = useState(false);
  const [closeStepPage, setCloseStepPage] = useState(1);
  const [closeStepForm, setCloseStepForm] = useState({ description: '', comment: '' });
  const [completePreviewItems, setCompletePreviewItems] = useState([]);
  const [showRenumberModal, setShowRenumberModal] = useState(false);
  const [renumberItems, setRenumberItems] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // ── Datasheet state ──────────────────────────────────────────────────────────
  const [dsFilters, setDsFilters] = useState({ status: ['Not Started', 'In Progress'] });
  const [dsEditMode, setDsEditMode] = useState(false);
  const [dsEdits, setDsEdits] = useState({});
  const [dsMenuOpen, setDsMenuOpen] = useState(false);
  const dsMenuRef = useRef(null);
  const [dsSortPending, setDsSortPending] = useState(false);
  const [showDescModal, setShowDescModal] = useState(false);
  const [descModalStep, setDescModalStep] = useState(null);
  const [descModalEditMode, setDescModalEditMode] = useState(false);
  const [descLocal, setDescLocal] = useState({ description: '', comment: '' });

  // ── Data loading ─────────────────────────────────────────────────────────────
  const loadDevPlan = useCallback(async () => {
    try {
      setLoading(true);
      const response = await devFilesApi.getDevPlan();
      setDevPlan(response.data);
      setSelectedStep(prev => {
        if (prev) return prev;
        const incomplete = response.data.steps.find(s => s.status !== 'Complete');
        return incomplete || response.data.steps[0] || null;
      });
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load dev plan');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { loadDevPlan(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (devPlan && selectedStep) {
      const updated = devPlan.steps.find(s => s.step === selectedStep.step);
      if (updated) setSelectedStep(updated);
      else if (devPlan.steps.length > 0) setSelectedStep(devPlan.steps[0]);
      else setSelectedStep(null);
    }
  }, [devPlan]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (dsMenuRef.current && !dsMenuRef.current.contains(e.target)) setDsMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── List view handlers ───────────────────────────────────────────────────────
  const handleSelectStep = (step) => {
    if (isEditing) {
      notify.confirm('Discard unsaved changes?', () => {
        setIsEditing(false);
        setEditForm({});
        setSelectedStep(step);
      });
      return;
    }
    setSelectedStep(step);
  };

  const startEditing = () => {
    if (!selectedStep) return;
    setEditForm({ ...selectedStep });
    setIsEditing(true);
  };

  const cancelEditing = () => { setIsEditing(false); setEditForm({}); };

  const handleSave = async () => {
    try {
      setSaving(true);
      await devFilesApi.updateDevPlanStep(editForm.step, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStep = async () => {
    if (!newStep.name.trim()) return;
    try {
      setSaving(true);
      await devFilesApi.addDevPlanStep(newStep);
      setNewStep({ name: '', description: '', comment: '', status: 'Not Started', type: 'Core', owner: currentUserSlug });
      setShowAddStep(false);
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add step');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = () => {
    if (!selectedStep) return;
    notify.confirm(`Delete Step ${selectedStep.step}: ${selectedStep.name}?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteDevPlanStep(selectedStep.step);
        setIsEditing(false);
        setSelectedStep(null);
        await loadDevPlan();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete step');
      } finally {
        setSaving(false);
      }
    });
  };

  const openCloseStepModal = () => {
    if (!selectedStep || !devPlan?.steps) return;
    setCloseStepPage(1);
    setCloseStepForm({ description: selectedStep.description || '', comment: selectedStep.comment || '' });
    // Compute renumber preview immediately
    const completingStep = devPlan.steps.find(s => s.step === selectedStep.step);
    if (!completingStep) return;
    const completed = devPlan.steps.filter(s => s.status === 'Complete').sort((a, b) => a.step - b.step);
    const active = devPlan.steps.filter(s => s.status !== 'Complete' && s.step !== selectedStep.step).sort((a, b) => a.step - b.step);
    const result = [];
    let seq = 1;
    completed.forEach(s => result.push({ ...s, newStep: seq++ }));
    result.push({ ...completingStep, status: 'Complete', newStep: seq++ });
    active.forEach(s => result.push({ ...s, newStep: seq++ }));
    setCompletePreviewItems(result);
    setShowCloseStep(true);
  };

  const handleApplyComplete = async () => {
    if (!selectedStep) return;
    try {
      setSaving(true);
      // Save any description/notes edits made in the complete step modal
      if (closeStepForm.description !== (selectedStep.description || '') || closeStepForm.comment !== (selectedStep.comment || '')) {
        await devFilesApi.updateDevPlanStep(selectedStep.step, { ...selectedStep, description: closeStepForm.description, comment: closeStepForm.comment });
      }
      const summary = `Step ${selectedStep.step}: ${selectedStep.name}`;
      const changes = closeStepForm.description?.split('\n').map(c => c.trim()).filter(c => c.length > 0) || [];
      await devFilesApi.closeDevPlanStep(selectedStep.step, summary, changes);
      const updates = completePreviewItems.map(item => ({ id: item.id, step: item.newStep, owner: item.owner }));
      await devFilesApi.reorderDevPlan(updates);
      setShowCloseStep(false);
      setCloseStepForm({ description: '', comment: '' });
      window.dispatchEvent(new Event('pmd-version-updated'));
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to complete step');
      setSaving(false);
    }
  };

  // ── Datasheet handlers ───────────────────────────────────────────────────────
  const handleDsEdit = (id, field, value) => {
    setDsEdits(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }));
  };

  const handleDsResort = () => {
    const allItems = devPlan.steps.map(s => ({
      id: s.id,
      step: s.step,
      newStep: dsEdits[s.id]?.step ?? s.step,
      edited: dsEdits[s.id]?.step !== undefined
    }));
    // Edited items win ties — they get their target position; incumbents get bumped
    const sorted = [...allItems].sort((a, b) => {
      if (a.newStep !== b.newStep) return a.newStep - b.newStep;
      if (a.edited && !b.edited) return -1;
      if (!a.edited && b.edited) return 1;
      return a.step - b.step;
    });
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].newStep <= sorted[i - 1].newStep) {
        sorted[i] = { ...sorted[i], newStep: sorted[i - 1].newStep + 1 };
      }
    }
    setDsEdits(prev => {
      const next = { ...prev };
      sorted.forEach(item => {
        if (item.newStep !== item.step) {
          next[item.id] = { ...(next[item.id] || {}), step: item.newStep };
        } else if (next[item.id]) {
          const { step, ...rest } = next[item.id]; // eslint-disable-line no-unused-vars
          if (Object.keys(rest).length === 0) delete next[item.id];
          else next[item.id] = rest;
        }
      });
      return next;
    });
  };

  const handleDsCancel = () => { setDsEdits({}); setDsEditMode(false); setDsSortPending(false); };

  const openRenumberModal = () => {
    if (!devPlan?.steps) return;
    const sorted = [...devPlan.steps].sort((a, b) => a.step - b.step);
    setRenumberItems(sorted.map((s, i) => ({ ...s, newStep: i + 1 })));
    setShowRenumberModal(true);
  };

  const handleApplyRenumber = async () => {
    try {
      setSaving(true);
      const updates = renumberItems.map(item => ({ id: item.id, step: item.newStep, owner: item.owner }));
      await devFilesApi.reorderDevPlan(updates);
      setShowRenumberModal(false);
      await loadDevPlan();
    } catch (err) {
      notify.error('Failed to renumber steps');
      setSaving(false);
    }
  };

  const handleDsSave = async () => {
    const dirtyIds = Object.keys(dsEdits);
    if (dirtyIds.length === 0) return;
    try {
      setSaving(true);
      const hasStepChanges = dirtyIds.some(id => dsEdits[id].step !== undefined);

      // Non-step field changes — use original step number as key, send full merged object
      const fieldDirty = dirtyIds.filter(id => {
        const e = dsEdits[id];
        return e.name !== undefined || e.type !== undefined || e.status !== undefined ||
               e.owner !== undefined || e.description !== undefined || e.comment !== undefined;
      });
      for (const id of fieldDirty) {
        const original = devPlan.steps.find(s => s.id === parseInt(id));
        if (!original) continue;
        const merged = { ...original, ...dsEdits[id] };
        await devFilesApi.updateDevPlanStep(original.step, merged);
      }

      // Step reorder
      if (hasStepChanges) {
        const updates = devPlan.steps.map(s => ({
          id: s.id,
          step: dsEdits[s.id]?.step ?? s.step,
          owner: dsEdits[s.id]?.owner ?? s.owner
        }));
        await devFilesApi.reorderDevPlan(updates);
      }

      setDsEdits({});
      setDsEditMode(false);
      setDsSortPending(false);
      notify.success('Changes saved');
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const openDescModal = (step, editMode) => {
    const merged = { ...step, ...(dsEdits[step.id] || {}) };
    setDescModalStep(step);
    setDescLocal({ description: merged.description || '', comment: merged.comment || '' });
    setDescModalEditMode(editMode);
    setShowDescModal(true);
  };

  const handleDescApply = () => {
    if (!descModalStep) return;
    setDsEdits(prev => ({
      ...prev,
      [descModalStep.id]: { ...(prev[descModalStep.id] || {}), ...descLocal }
    }));
    setShowDescModal(false);
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const dsHasStepChanges = Object.values(dsEdits).some(e => e.step !== undefined);
  const dsDisplayData = useMemo(() => {
    if (!devPlan?.steps) return [];
    let rows = dsSortPending
      ? [...devPlan.steps].sort((a, b) => (dsEdits[a.id]?.step ?? a.step) - (dsEdits[b.id]?.step ?? b.step))
      : devPlan.steps;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      rows = rows.filter(s =>
        s.name?.toLowerCase().includes(term) ||
        String(s.step).includes(term) ||
        s.owner?.toLowerCase().includes(term) ||
        s.description?.toLowerCase().includes(term)
      );
    }
    return rows;
  }, [devPlan, dsSortPending, dsEdits, searchTerm]);
  const dsIsDirty = Object.keys(dsEdits).length > 0;
  const dsHasFilters = Object.values(dsFilters).some(f => f?.length > 0);

  // Filtered count for the clear-filter button
  const dsFilteredCount = useMemo(() => {
    if (!devPlan?.steps) return 0;
    return devPlan.steps.filter(row =>
      Object.entries(dsFilters).every(([key, vals]) => !vals || vals.length === 0 || vals.includes(String(row[key] || '')))
    ).length;
  }, [devPlan, dsFilters]);

  // Columns for FilterableDataGrid
  const dsColumns = useMemo(() => [
    { key: 'step',   fieldName: 'step',   label: '#',      width: 60  },
    { key: 'name',   fieldName: 'name',   label: 'Name',   width: 520 },
    { key: 'type',   fieldName: 'type',   label: 'Type',   width: 110, formatter: (v) => v ? <span className={`step-type-badge ${getTypeClass(v)}`}>{v}</span> : null },
    { key: 'status', fieldName: 'status', label: 'Status', width: 120, formatter: (v) => v ? <span className={`step-status-badge ${getStatusClass(v)}`}>{v}</span> : null },
    { key: 'owner',  fieldName: 'owner',  label: 'Owner',  width: 100, formatter: (v) => formatOwner(v) },
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  // Editable cell renderer for datasheet edit mode
  const renderDsCell = (row, col, displayValue) => {
    switch (col.key) {
      case 'step':
        return (
          <input
            type="number"
            className="ds-cell-input"
            value={displayValue ?? ''}
            onChange={e => handleDsEdit(row.id, 'step', parseInt(e.target.value) || row.step)}
            min={1}
            style={{ width: '48px', textAlign: 'center' }}
          />
        );
      case 'name':
        return <input className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'name', e.target.value)} style={{ width: '100%' }} />;
      case 'type':
        return (
          <select className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'type', e.target.value)}>
            {STEP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        );
      case 'status':
        if (row.status === 'Complete') return <span className="step-status-badge status-complete">Complete</span>;
        return (
          <select className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'status', e.target.value)}>
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
          </select>
        );
      case 'owner':
        return (
          <select className="ds-cell-input" value={displayValue ?? ''} onChange={e => handleDsEdit(row.id, 'owner', e.target.value || null)}>
            <option value="">Unassigned</option>
            {OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      default:
        return displayValue;
    }
  };

  const getProgress = () => {
    if (!devPlan?.steps) return { complete: 0, total: 0, percent: 0 };
    const complete = devPlan.steps.filter(s => s.status === 'Complete').length;
    const total = devPlan.steps.length;
    return { complete, total, percent: total ? Math.round((complete / total) * 100) : 0 };
  };

  const getListSteps = () => {
    if (!devPlan?.steps) return [];
    let filtered = devPlan.steps.filter(s => s.status !== 'Complete');
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(term) ||
        String(s.step).includes(term) ||
        s.owner?.toLowerCase().includes(term) ||
        s.description?.toLowerCase().includes(term)
      );
    }
    return filtered;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Complete': return 'status-complete';
      case 'In Progress': return 'status-in-progress';
      default: return 'status-incomplete';
    }
  };

  const getTypeClass = (type) => {
    switch (type) {
      case 'Core': return 'type-core';
      case 'Feature': return 'type-feature';
      case 'Milestone': return 'type-milestone';
      case 'Infrastructure': return 'type-infrastructure';
      default: return 'type-core';
    }
  };

  const formatOwner = (owner) => owner ? owner.charAt(0).toUpperCase() + owner.slice(1).toLowerCase() : 'Unassigned';


  const hasChanges = isEditing && editForm && selectedStep && (
    editForm.name !== selectedStep.name ||
    editForm.description !== selectedStep.description ||
    editForm.comment !== selectedStep.comment ||
    editForm.status !== selectedStep.status ||
    editForm.type !== selectedStep.type ||
    editForm.owner !== selectedStep.owner
  );

  if (loading) return <div className="dev-page loading">Loading...</div>;

  const progress = getProgress();
  const listSteps = getListSteps();

  return (
    <div className="module-container">
      {/* ── Header ── */}
      <div className="module-header">
        <div className="module-header-left">
          <ClipboardList size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Development Plan</h1>
          <div className="module-header-tabs">
            <button onClick={() => setViewMode('list')} className={`tab-btn-icon ${viewMode === 'list' ? 'active' : ''}`} title="List View"><List size={18} /></button>
            <button onClick={() => setViewMode('datasheet')} className={`tab-btn-icon ${viewMode === 'datasheet' ? 'active' : ''}`} title="Datasheet View"><Table size={18} /></button>
          </div>
          <span className="module-header-meta">
            {progress.total} steps &bull; {progress.complete} complete ({progress.percent}%)
          </span>
        </div>
        <div className="module-header-actions">
          {/* List view CRUD */}
          {viewMode === 'list' && (
            <>
              {isEditing
                ? <button className="crud-btn" onClick={handleDeleteStep} disabled={saving} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
                : <button className="crud-btn" onClick={() => { setNewStep(prev => ({ ...prev, owner: currentUserSlug })); setShowAddStep(true); }} title="Add Step"><Plus size={18} /></button>
              }
              <button className={`crud-btn ${!selectedStep && !isEditing ? 'disabled' : ''}`} onClick={isEditing ? cancelEditing : startEditing} disabled={!selectedStep && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`} onClick={handleSave} disabled={!isEditing || saving || !hasChanges} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Datasheet controls */}
          {viewMode === 'datasheet' && (
            <>
              <button className="crud-btn" onClick={dsEditMode ? handleDsCancel : () => setDsEditMode(true)} title={dsEditMode ? 'Cancel' : 'Edit'}>
                {dsEditMode ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`crud-btn ${!dsIsDirty || saving ? 'disabled' : ''}`} onClick={handleDsSave} disabled={!dsIsDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}

          {/* Clear filter — always visible when filters active */}
          {dsHasFilters && (
            <button onClick={() => setDsFilters({})} className="clear-filter-btn" title="Clear filters">
              <FilterX size={16} /><span>{dsFilteredCount}/{devPlan?.steps?.length || 0}</span>
            </button>
          )}

          {/* Search */}
          <div className="search-wrapper">
            <Search size={16} className="search-icon" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search steps..." className="search-input" />
          </div>


          {/* Action menu — list: Refresh + Complete Step; datasheet: Refresh */}
          <div className="action-menu" ref={viewMode === 'list' ? menuRef : dsMenuRef}>
            <button className="action-menu-btn" onClick={() => viewMode === 'list' ? setMenuOpen(!menuOpen) : setDsMenuOpen(!dsMenuOpen)} title="Actions"><GripVertical size={16} /></button>
            {viewMode === 'list' && menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); loadDevPlan(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                {selectedStep && selectedStep.status !== 'Complete' && (
                  <button className="action-menu-item" onClick={() => { setMenuOpen(false); openCloseStepModal(); }}>
                    <Check size={16} /><span>Complete Step</span>
                  </button>
                )}
              </div>
            )}
            {viewMode === 'datasheet' && dsMenuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setDsMenuOpen(false); loadDevPlan(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className="action-menu-item" onClick={() => { setDsMenuOpen(false); openRenumberModal(); }}>
                  <ListOrdered size={16} /><span>Renumber Steps</span>
                </button>
                {dsHasStepChanges && (
                  <button className="action-menu-item" onClick={() => { setDsMenuOpen(false); handleDsResort(); }}>
                    <ListOrdered size={16} /><span>Apply Step Edits</span>
                  </button>
                )}
                {dsHasStepChanges && (
                  <button className="action-menu-item" onClick={() => { setDsMenuOpen(false); setDsSortPending(p => !p); }}>
                    <ArrowUpDown size={16} /><span>{dsSortPending ? 'Reset View Order' : 'Sort by Pending'}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="dev-plan-progress">
        <div className="dev-plan-progress-bar" style={{ width: `${progress.percent}%` }} />
      </div>

      {/* ── List View ── */}
      {viewMode === 'list' && (
        <div className={`list-view ${listCollapsed ? 'list-collapsed' : ''}`}>
          {listCollapsed ? (
            <div className="list-panel-collapsed">
              <button className="list-panel-expand-btn" onClick={() => setListCollapsed(false)} title="Expand list">
                <ChevronRight size={16} />
              </button>
            </div>
          ) : (
            <div className="list-panel">
              <div className="list-panel-header">
                <button className="list-panel-collapse-btn" onClick={() => setListCollapsed(true)} title="Collapse list">
                  <ChevronLeft size={16} />
                </button>
              </div>
              <div className="list-scroll">
                {listSteps.map(step => (
                  <button
                    key={step.id}
                    onClick={() => handleSelectStep(step)}
                    className={`list-item-btn ${selectedStep?.step === step.step ? 'selected' : ''}`}
                    style={{  }}
                  >
                    <span className="list-item-primary">
                      <span style={{ color: 'var(--color-gray-400)', marginRight: '6px', fontSize: 'var(--text-xs)' }}>{step.step}</span>
                      {step.name}
                    </span>
                    <span className="list-item-secondary">
                      {step.owner || 'Unassigned'} &bull; {step.status}
                    </span>
                  </button>
                ))}
                {listSteps.length === 0 && <div className="task-list-empty">No steps match</div>}
              </div>
            </div>
          )}

          <div className="detail-panel">
            {selectedStep ? (
              <div className="detail-content">
                <div className="detail-grid-24">

                  {/* General Information */}
                  <div className="col-span-24">
                    <div className="detail-section-header">General Information</div>
                  </div>

                  <div className="form-group col-span-4">
                    <label>Step</label>
                    <input type="text" className="form-input" value={selectedStep.step} readOnly onChange={() => {}} />
                  </div>
                  <div className="form-group col-span-20">
                    <label>Name</label>
                    {isEditing
                      ? <input type="text" className="form-input" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} autoFocus />
                      : <input type="text" className="form-input" value={selectedStep.name} readOnly onChange={() => {}} />
                    }
                  </div>

                  <div className="form-group col-span-8">
                    <label>Type</label>
                    {isEditing
                      ? <select className="form-input" value={editForm.type || 'Core'} onChange={e => setEditForm({ ...editForm, type: e.target.value })}>
                          {STEP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      : <span className={`step-type-badge ${getTypeClass(selectedStep.type)}`}>{selectedStep.type || 'Core'}</span>
                    }
                  </div>
                  <div className="form-group col-span-8">
                    <label>Status</label>
                    {isEditing
                      ? <select className="form-input" value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                        </select>
                      : <span className={`step-status-badge ${getStatusClass(selectedStep.status)}`}>{selectedStep.status}</span>
                    }
                  </div>
                  <div className="form-group col-span-8">
                    <label>Owner</label>
                    {isEditing
                      ? <select className="form-input" value={editForm.owner || ''} onChange={e => setEditForm({ ...editForm, owner: e.target.value || null })}>
                          <option value="">Unassigned</option>
                          {OWNERS.map(o => <option key={o} value={o}>{formatOwner(o)}</option>)}
                        </select>
                      : <span className="step-owner-badge">{formatOwner(selectedStep.owner)}</span>
                    }
                  </div>

                  {/* Step Details */}
                  <div className="col-span-24">
                    <div className="detail-section-header">Step Details</div>
                  </div>

                  <div className="form-group col-span-24">
                    <label>Description</label>
                    {isEditing
                      ? <textarea className="form-input" value={editForm.description || ''} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={5} style={{ resize: 'vertical' }} />
                      : <textarea className="form-input" value={selectedStep.description || ''} readOnly rows={5} onChange={() => {}} style={{ resize: 'vertical', overflowY: 'auto' }} />
                    }
                  </div>

                  <div className="form-group col-span-24">
                    <label>Notes</label>
                    {isEditing
                      ? <textarea className="form-input" value={editForm.comment || ''} onChange={e => setEditForm({ ...editForm, comment: e.target.value })} rows={8} style={{ resize: 'vertical' }} placeholder="Additional details, sub-tasks, or implementation notes..." />
                      : <textarea className="form-input" value={selectedStep.comment || ''} readOnly rows={8} onChange={() => {}} style={{ resize: 'vertical', overflowY: 'auto' }} />
                    }
                  </div>

                </div>
              </div>
            ) : (
              <div className="dev-detail-empty"><p>Select a step to view details</p></div>
            )}
          </div>
        </div>
      )}

      {/* ── Datasheet View ── */}
      {viewMode === 'datasheet' && (
        <div className="datasheet-view">
          <FilterableDataGrid
            data={dsDisplayData}
            allData={devPlan?.steps || []}
            columns={dsColumns}
            filters={dsFilters}
            setFilters={setDsFilters}
            editModeEnabled={dsEditMode}
            editedData={dsEdits}
            renderEditableCell={renderDsCell}
            onRowDoubleClick={(row) => openDescModal(row, dsEditMode)}
          />
        </div>
      )}

      {/* ── Add Step Modal ── */}
      {showAddStep && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal">
            <div className="modal-header">
              <h3>New Step</h3>
              <button onClick={() => setShowAddStep(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="detail-grid-24" style={{ marginBottom: 0 }}>
                <div className="col-span-24">
                  <div className="detail-section-header" style={{ marginTop: 0 }}>General Information</div>
                </div>
                <div className="form-group col-span-24">
                  <label>Name</label>
                  <input type="text" className="form-input" value={newStep.name} onChange={e => setNewStep({ ...newStep, name: e.target.value })} placeholder="Step name" autoFocus />
                </div>
                <div className="form-group col-span-8">
                  <label>Type</label>
                  <select className="form-input" value={newStep.type} onChange={e => setNewStep({ ...newStep, type: e.target.value })}>
                    {STEP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group col-span-8">
                  <label>Status</label>
                  <select className="form-input" value={newStep.status} onChange={e => setNewStep({ ...newStep, status: e.target.value })}>
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                  </select>
                </div>
                <div className="form-group col-span-8">
                  <label>Owner</label>
                  <select className="form-input" value={newStep.owner || ''} onChange={e => setNewStep({ ...newStep, owner: e.target.value || null })}>
                    <option value="">Unassigned</option>
                    {OWNERS.map(o => <option key={o} value={o}>{formatOwner(o)}</option>)}
                  </select>
                </div>
                <div className="col-span-24">
                  <div className="detail-section-header">Step Details</div>
                </div>
                <div className="form-group col-span-24">
                  <label>Description</label>
                  <textarea className="form-input" value={newStep.description || ''} onChange={e => setNewStep({ ...newStep, description: e.target.value })} placeholder="What does this step involve?" rows={5} style={{ resize: 'vertical' }} />
                </div>
                <div className="form-group col-span-24">
                  <label>Notes</label>
                  <textarea className="form-input" value={newStep.comment || ''} onChange={e => setNewStep({ ...newStep, comment: e.target.value })} placeholder="Additional details or sub-tasks" rows={5} style={{ resize: 'vertical' }} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddStep(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddStep} disabled={saving || !newStep.name.trim()}>{saving ? 'Adding...' : 'Add Step'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Complete Step Modal (2-page wizard) ── */}
      {showCloseStep && selectedStep && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal" style={{ maxWidth: '700px', height: '680px' }}>
            <div className="modal-header">
              <h3>{closeStepPage === 1 ? 'Complete Step — Details' : 'Complete Step — Renumbering'}</h3>
              <button onClick={() => { setShowCloseStep(false); setCloseStepPage(1); }}><X size={20} /></button>
            </div>

            {closeStepPage === 1 ? (
              <>
                <div className="modal-body">
                  <div className="detail-grid-24" style={{ marginBottom: 0 }}>
                    {/* General Information */}
                    <div className="col-span-24">
                      <div className="detail-section-header" style={{ marginTop: 0 }}>General Information</div>
                    </div>
                    <div className="form-group col-span-4">
                      <label>Step</label>
                      <input type="text" className="form-input" value={selectedStep.step} readOnly onChange={() => {}} />
                    </div>
                    <div className="form-group col-span-20">
                      <label>Name</label>
                      <input type="text" className="form-input" value={selectedStep.name} readOnly onChange={() => {}} />
                    </div>
                    <div className="form-group col-span-12">
                      <label>Type</label>
                      <span className={`step-type-badge ${getTypeClass(selectedStep.type)}`}>{selectedStep.type || 'Core'}</span>
                    </div>
                    <div className="form-group col-span-12">
                      <label>Status</label>
                      <span className={`step-status-badge ${getStatusClass(selectedStep.status)}`}>{selectedStep.status}</span>
                    </div>
                    {/* Step Details */}
                    <div className="col-span-24">
                      <div className="detail-section-header">Step Details</div>
                    </div>
                    <div className="form-group col-span-24">
                      <label>Description</label>
                      <textarea className="form-input" value={closeStepForm.description} onChange={e => setCloseStepForm({ ...closeStepForm, description: e.target.value })} rows={4} style={{ resize: 'vertical' }} />
                    </div>
                    <div className="form-group col-span-24">
                      <label>Notes</label>
                      <textarea className="form-input" value={closeStepForm.comment} onChange={e => setCloseStepForm({ ...closeStepForm, comment: e.target.value })} rows={4} style={{ resize: 'vertical' }} />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => setCloseStepPage(2)}>Next</button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column' }}>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)', margin: '0 0 var(--spacing-sm)', flexShrink: 0 }}>
                    Step {selectedStep.step} moves to position {completePreviewItems.find(i => i.id === selectedStep.id)?.newStep || '?'} — all steps renumber sequentially. {completePreviewItems.filter(i => i.step !== i.newStep).length} step(s) will renumber.
                  </p>
                  <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    <table className="data-grid" style={{ fontSize: 'var(--text-xs)' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '50px' }}>Step</th>
                          <th style={{ width: '50px' }}>New</th>
                          <th>Name</th>
                          <th style={{ width: '80px' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {completePreviewItems.map(item => {
                          const changed = item.step !== item.newStep;
                          const isCompleting = item.id === selectedStep.id;
                          return (
                            <tr key={item.id} style={{ backgroundColor: isCompleting ? 'var(--color-success-light)' : changed ? '#fffde7' : undefined, fontWeight: isCompleting ? 600 : undefined }}>
                              <td style={{ textAlign: 'center', color: 'var(--color-gray-400)' }}>{item.step}</td>
                              <td style={{ textAlign: 'center', fontWeight: changed ? 600 : 400, color: changed ? 'var(--color-primary)' : undefined }}>{item.newStep}</td>
                              <td>{item.name}</td>
                              <td style={{ color: isCompleting ? 'var(--color-success)' : 'var(--color-gray-500)' }}>{isCompleting ? 'Complete' : item.status}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setCloseStepPage(1)}>Back</button>
                  <button className="btn btn-primary" onClick={handleApplyComplete} disabled={saving}>{saving ? 'Completing...' : 'Complete & Renumber'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Renumber Steps Modal ── */}
      {showRenumberModal && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal" style={{ maxWidth: '700px', height: '680px' }}>
            <div className="modal-header">
              <h3>Renumber Steps</h3>
              <button onClick={() => setShowRenumberModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)', margin: '0 0 var(--spacing-sm)', flexShrink: 0 }}>
                All steps will be renumbered sequentially based on their current order. {renumberItems.filter(i => i.step !== i.newStep).length} step(s) will change.
              </p>
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <table className="data-grid" style={{ fontSize: 'var(--text-xs)' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Step</th>
                      <th style={{ width: '50px' }}>New</th>
                      <th>Name</th>
                      <th style={{ width: '80px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renumberItems.map(item => {
                      const changed = item.step !== item.newStep;
                      return (
                        <tr key={item.id} style={{ backgroundColor: changed ? '#fffde7' : undefined }}>
                          <td style={{ textAlign: 'center', color: 'var(--color-gray-400)' }}>{item.step}</td>
                          <td style={{ textAlign: 'center', fontWeight: changed ? 600 : 400, color: changed ? 'var(--color-primary)' : undefined }}>{item.newStep}</td>
                          <td>{item.name}</td>
                          <td style={{ color: 'var(--color-gray-500)' }}>{item.status}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={handleApplyRenumber} disabled={saving}>{saving ? 'Renumbering...' : 'Apply Renumbering'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Description Modal ── */}
      {showDescModal && descModalStep && (
        <div className="modal-overlay">
          <div className="modal-content dev-plan-modal" style={{ maxWidth: '680px', height: '560px' }}>
            <div className="modal-header">
              <h3>Step Details — {descModalStep.step} — {descModalStep.name}</h3>
              <button onClick={() => setShowDescModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Description</label>
                {descModalEditMode ? (
                  <textarea className="form-input" value={descLocal.description} onChange={e => setDescLocal(p => ({ ...p, description: e.target.value }))} rows={10} autoFocus style={{ resize: 'vertical' }} />
                ) : (
                  <textarea className="form-input" value={descLocal.description || ''} readOnly rows={10} onChange={() => {}} style={{ resize: 'vertical' }} />
                )}
              </div>
              <div className="form-group">
                <label>Notes</label>
                {descModalEditMode ? (
                  <textarea className="form-input" value={descLocal.comment} onChange={e => setDescLocal(p => ({ ...p, comment: e.target.value }))} rows={10} placeholder="Additional details, sub-tasks, or implementation notes..." style={{ resize: 'vertical' }} />
                ) : (
                  <textarea className="form-input" value={descLocal.comment || ''} readOnly rows={10} onChange={() => {}} style={{ resize: 'vertical' }} />
                )}
              </div>
            </div>
            {descModalEditMode && (
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDescModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleDescApply}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DevPlanPage;
